import _ from 'lodash';
import http from '../http';

/**
 * 获取资源图集合。
 * OpenAPI：`GET /v1/public/{string_id}/restaurant-static/bundled-image-urls`（summary：获取资源图集合；
 * tags：`Dambuster/shark-cloud/RBP`；`security: []`，鉴权走 Header：`authorization-type: BASIC_AUTH`、`Authorization: Bearer <token>`，与 `http` 拦截器一致）。
 * Apifox 文档中响应 `application/json` 的 schema 未展开字段，故返回 `unknown`，业务侧拿到数据后再收窄类型或写解析函数。
 * @param stringId - 路径参数 `string_id`（餐厅 string id）
 * @returns 接口 200 的 JSON 体
 */
async function get_restaurant_static_bundled_image_urls(stringId: string): Promise<unknown> {
    return await http.get(`/public/${encodeURIComponent(stringId)}/restaurant-static/bundled-image-urls`);
}

/**
 * 上传菜单图 POST /v1/public/{string_id}/restaurant-static/menu-image
 * Apifox：`file_name` 为 **query** 必填（如 `5.jpg`），body 为 multipart 文件字段 `file`。
 */
async function post_restaurant_static_menu_image(
    stringId: string,
    file: File,
    queryFileName: string,
): Promise<unknown> {
    const fd = new FormData();
    fd.append('', file);
    return await http.post(`/public/${encodeURIComponent(stringId)}/restaurant-static/menu-image`, fd, {
        params: { file_name: queryFileName },
        headers: { 'Content-Type': 'multipart/form-data' },
    });
}

/**
 * 删除菜单图 DELETE /v1/public/{string_id}/restaurant-static/menu-image/{file_name}
 */
async function delete_restaurant_static_menu_image(stringId: string, fileName: string): Promise<unknown> {
    return await http.delete(
        `/public/${encodeURIComponent(stringId)}/restaurant-static/menu-image/${encodeURIComponent(fileName)}`,
    );
}

/**
 * 上传菜品图 POST /v1/public/{string_id}/restaurant-static/dish-image
 * Apifox：query `file_name` 必填（如 `4.jpg`），body 为 multipart `file`。
 */
async function post_restaurant_static_dish_image(
    stringId: string,
    file: File,
    queryFileName: string,
): Promise<unknown> {
    const fd = new FormData();
    fd.append('', file);
    return await http.post(`/public/${encodeURIComponent(stringId)}/restaurant-static/dish-image`, fd, {
        params: { file_name: queryFileName },
        headers: { 'Content-Type': 'multipart/form-data' },
    });
}

/**
 * 删除菜品图 DELETE /v1/public/{string_id}/restaurant-static/dish-image/{file_name}
 */
async function delete_restaurant_static_dish_image(stringId: string, fileName: string): Promise<unknown> {
    return await http.delete(
        `/public/${encodeURIComponent(stringId)}/restaurant-static/dish-image/${encodeURIComponent(fileName)}`,
    );
}

/**
 * 上传 MOP 首屏轮播图 POST `/v1/public/{string_id}/restaurant-static/bulletin-mop-carousel-image/{index}`
 * Apifox：与 `menu-image` 一致，query **`file_name`** 必填；body 为 multipart，字段名为空串（与 OSS 解析一致）。
 * @param stringId - 餐厅 `string_id`（路径）
 * @param index - 轮播槽位下标（与设置 56–60 及原 v3 `mop-carousel/image/{index}` 语义一致）
 * @param file - 图片文件
 * @param queryFileName - query `file_name`，如 `0.jpg`
 */
async function post_restaurant_static_bulletin_mop_carousel_image(
    stringId: string,
    index: number,
    file: File,
    queryFileName: string,
): Promise<unknown> {
    const fd = new FormData();
    fd.append('', file);
    return await http.post(
        `/public/${encodeURIComponent(stringId)}/restaurant-static/bulletin-mop-carousel-image/${encodeURIComponent(String(index))}`,
        fd,
        {
            params: { file_name: queryFileName },
            headers: { 'Content-Type': 'multipart/form-data' },
        },
    );
}

/**
 * 删除 MOP 首屏轮播图 DELETE `/v1/public/{string_id}/restaurant-static/bulletin-mop-carousel-image/{index}`
 * 与 Dambuster 一致：query **`file_name` 必填**，为当前槽位在设置/bundled 中保存的 basename（上传接口返回的 `0_<uuid>.jpg`）。
 * @param stringId - 餐厅 `string_id`
 * @param index - 轮播槽位下标 0–4
 * @param fileName - query `file_name`，如 `0_a1b2c3d4-....jpg`
 */
async function delete_restaurant_static_bulletin_mop_carousel_image(
    stringId: string,
    index: number,
    fileName: string,
): Promise<unknown> {
    return await http.delete(
        `/public/${encodeURIComponent(stringId)}/restaurant-static/bulletin-mop-carousel-image/${encodeURIComponent(String(index))}`,
        { params: { file_name: fileName } },
    );
}

/**
 * 上传餐厅活动公告图 `POST /v1/public/{string_id}/restaurant-static/bulletin-activity-image`
 * OpenAPI：query **`name`** 必填；body multipart 文件字段名为空串（与菜单图一致）。
 * @param stringId - 餐厅 `string_id`
 * @param file - 图片文件
 * @param queryName - query `name`，如 `activity-bulletin1.jpg`
 */
async function post_restaurant_static_bulletin_activity_image(
    stringId: string,
    file: File,
    queryName: string,
): Promise<unknown> {
    const fd = new FormData();
    fd.append('', file);
    return await http.post(
        `/public/${encodeURIComponent(stringId)}/restaurant-static/bulletin-activity-image`,
        fd,
        {
            params: { name: queryName },
            headers: { 'Content-Type': 'multipart/form-data' },
        },
    );
}

/**
 * 删除餐厅活动公告图 `DELETE /v1/public/{string_id}/restaurant-static/bulletin-activity-image/{name}`
 * @param stringId - 餐厅 `string_id`
 * @param name - 路径参数 `name`（文件名，如 `activity-bulletin1.jpg`）
 */
async function delete_restaurant_static_bulletin_activity_image(stringId: string, name: string): Promise<unknown> {
    return await http.delete(
        `/public/${encodeURIComponent(stringId)}/restaurant-static/bulletin-activity-image/${encodeURIComponent(name)}`,
    );
}

/**
 * 上传餐厅环境图 `POST /v1/public/{string_id}/restaurant-static/bulletin-background-image`
 * @param stringId - 餐厅 `string_id`
 * @param file - 图片文件
 */
async function post_restaurant_static_bulletin_background_image(stringId: string, file: File): Promise<unknown> {
    const fd = new FormData();
    fd.append('', file);
    return await http.post(
        `/public/${encodeURIComponent(stringId)}/restaurant-static/bulletin-background-image`,
        fd,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
        },
    );
}

/**
 * 上传图片就餐指南 `POST /v1/public/{string_id}/restaurant-static/bulletin-dining-guide-image?number=1`
 * 与后端一致：query **`number`** 为槽位 1–5，对应 OSS `bulletin{n}.jpg`（勿再传 `name`）。
 * @param stringId - 餐厅 `string_id`
 * @param file - 图片文件
 * @param number - 槽位序号 1..5
 */
async function post_restaurant_static_bulletin_dining_guide_image(
    stringId: string,
    file: File,
    number: number,
): Promise<unknown> {
    const fd = new FormData();
    fd.append('', file);
    return await http.post(`/public/${encodeURIComponent(stringId)}/restaurant-static/bulletin-dining-guide-image`, fd, {
        params: { number },
        headers: { 'Content-Type': 'multipart/form-data' },
    });
}

/**
 * 删除指定就餐指南图 `DELETE /v1/public/{string_id}/restaurant-static/bulletin-dining-guide-image/{number}`
 * @param stringId - 餐厅 `string_id`
 * @param number - 槽位序号（路径参数，与业务槽位 id 一致，如 `1`）
 */
async function delete_restaurant_static_bulletin_dining_guide_image(
    stringId: string,
    number: string | number,
): Promise<unknown> {
    return await http.delete(
        `/public/${encodeURIComponent(stringId)}/restaurant-static/bulletin-dining-guide-image/${encodeURIComponent(String(number))}`,
    );
}

/**
 * 上传 QOP 模板图 `POST /v1/public/{string_id}/restaurant-static/qop-mould-image/{id}`
 * @param stringId - 餐厅 `string_id`
 * @param id - 模板 id（与历史 v3 路径一致，多为数字字符串）
 * @param file - 图片文件
 */
async function post_restaurant_static_qop_mould_image(stringId: string, id: string, file: File): Promise<unknown> {
    const fd = new FormData();
    fd.append('', file);
    return await http.post(
        `/public/${encodeURIComponent(stringId)}/restaurant-static/qop-mould-image/${encodeURIComponent(id)}`,
        fd,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
        },
    );
}

/**
 * 删除 QOP 模板图 `DELETE /v1/public/{string_id}/restaurant-static/qop-mould-image/{id}`
 * @param stringId - 餐厅 `string_id`
 * @param id - 模板 id
 */
async function delete_restaurant_static_qop_mould_image(stringId: string, id: string): Promise<unknown> {
    return await http.delete(
        `/public/${encodeURIComponent(stringId)}/restaurant-static/qop-mould-image/${encodeURIComponent(id)}`,
    );
}

/**
 * 上传餐厅 QOP Logo `POST /v1/public/{string_id}/restaurant-static/qop-logo-image`
 * Body 与同类 restaurant-static 一致：multipart 空字段名。
 * @param stringId - 餐厅 `string_id`
 * @param file - 图片文件（建议 1:1）
 */
async function post_restaurant_static_qop_logo_image(stringId: string, file: File): Promise<unknown> {
    const fd = new FormData();
    fd.append('', file);
    return await http.post(
        `/public/${encodeURIComponent(stringId)}/restaurant-static/qop-logo-image`,
        fd,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
        },
    );
}

/**
 * 删除餐厅 QOP Logo `DELETE /v1/public/{string_id}/restaurant-static/qop-logo-image`
 * @param stringId - 餐厅 `string_id`
 */
async function delete_restaurant_static_qop_logo_image(stringId: string): Promise<unknown> {
    return await http.delete(`/public/${encodeURIComponent(stringId)}/restaurant-static/qop-logo-image`);
}

function parseRestaurantStaticUploadFileName(res: unknown): string | null {
    if (res == null) {
        return null;
    }
    if (typeof res === 'string') {
        const s = res.trim();
        return s || null;
    }
    if (typeof res !== 'object') {
        return null;
    }
    const r = res as Record<string, unknown>;
    const inner = (r.data !== undefined ? r.data : r.result !== undefined ? r.result : r) as Record<string, unknown>;
    const candidates = [
        _.get(inner, 'url'),
        _.get(inner, 'image_url'),
        _.get(inner, 'file_name'),
        _.get(inner, 'fileName'),
        _.get(inner, 'filename'),
        _.get(inner, 'name'),
        _.get(r, 'file_name'),
        _.get(r, 'url'),
    ];
    for (const c of candidates) {
        if (typeof c === 'string' && c.trim()) {
            return c.trim();
        }
    }
    return null;
}

export default {
    get_restaurant_static_bundled_image_urls,
    post_restaurant_static_menu_image,
    delete_restaurant_static_menu_image,
    post_restaurant_static_dish_image,
    delete_restaurant_static_dish_image,
    post_restaurant_static_bulletin_mop_carousel_image,
    delete_restaurant_static_bulletin_mop_carousel_image,
    post_restaurant_static_bulletin_activity_image,
    delete_restaurant_static_bulletin_activity_image,
    post_restaurant_static_bulletin_background_image,
    post_restaurant_static_bulletin_dining_guide_image,
    delete_restaurant_static_bulletin_dining_guide_image,
    post_restaurant_static_qop_mould_image,
    delete_restaurant_static_qop_mould_image,
    post_restaurant_static_qop_logo_image,
    delete_restaurant_static_qop_logo_image,
    parseRestaurantStaticUploadFileName,
};
